import BarekamText from "../../../../components/text";
import { MenuItem, Skeleton, Tooltip } from "@mui/material";
import React, { Dispatch, SetStateAction, useState } from "react";
import Message from "../../../../components/message";
import { MessageSections } from "../../../../components/message/message";
import { BarekamCard } from "../../../../components/card/card";
import { GapContainer } from "../../../../components/card/styled";
import { ThreeDot } from "../../../../components/three-dot/three-dot";
import recipientService from "../../../../../services/recipient.service";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import {
  changePagination,
  getMyRecipients,
} from "../../../../../redux/slices/recipients.slice";
import { ConfirmationDialog } from "../../../../components/dialogs/confirmation-dialog";
import BarekamChip from "../../../../components/chip/barekamChip";
import { InfoOutlined } from "@mui/icons-material";
import { getMessage } from "../../../../shared/helper-functions";
import { Recipient } from "../../../../shared/models/user/recipient/recipient";

export const RecipientCard = ({
  recipient,
  onClick,
  isReadonly,
  setRecipientToEdit,
}: RecipientCardProps) => {
  const { myRecipientsData } = useAppSelector(
    (state) => state.recipientDataReducer,
  );

  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  const [dialogOpen, setDialogOpen] = useState(false);

  const dispatch = useAppDispatch();

  const CardContent = () => (
    <>
      {recipient?.tid && (
        <GapContainer>
          <BarekamText variant={"s"} opaque style={{ minWidth: "130px" }}>
            <Message messageKey={"tid"} sectionId={MessageSections.USER} />
            {":"}
          </BarekamText>

          <BarekamText variant={"s"} maxWidth={15} width={"100%"}>
            {` ${recipient.tid}`}
          </BarekamText>
        </GapContainer>
      )}

      <GapContainer>
        <BarekamText variant={"s"} opaque style={{ minWidth: "130px" }}>
          <Message messageKey={"email"} sectionId={MessageSections.USER} />
          {":"}
        </BarekamText>

        <BarekamText variant={"s"} maxWidth={15} width={"100%"}>
          {` ${recipient.email}`}
        </BarekamText>
      </GapContainer>

      <GapContainer>
        <BarekamText variant={"s"} opaque style={{ minWidth: "130px" }}>
          <Message
            messageKey={"phone_number"}
            sectionId={MessageSections.USER}
          />
          {": "}
        </BarekamText>

        <BarekamText variant={"s"} maxWidth={15} width={"100%"}>
          {` +${recipient.phoneNumber}`}
        </BarekamText>
      </GapContainer>

      <GapContainer>
        <BarekamText variant={"s"} opaque style={{ minWidth: "130px" }}>
          <Message messageKey={"document"} sectionId={MessageSections.USER} />
          {": "}
        </BarekamText>

        <BarekamText variant={"s"} maxWidth={15} width={"100%"}>
          {recipient.documentType ? (
            <>
              <Message
                messageKey={recipient.documentType.name}
                sectionId={MessageSections.USER}
              />
              {", "}
            </>
          ) : (
            ""
          )}

          {recipient.passportOrIdSerialNumber}
        </BarekamText>
      </GapContainer>
    </>
  );

  const MainRecipientHeaderLabel = () => (
    <BarekamText variant={"1rem"} fontWeight={700}>
      {recipient.firstName} {recipient.lastName} (
      <Message messageKey={"you"} sectionId={MessageSections.USER} />)
    </BarekamText>
  );

  const RecipientHeaderLabel = () => (
    <BarekamText variant={"1rem"} fontWeight={700} width={"unset"}>
      {recipient.firstName} {recipient.lastName}
    </BarekamText>
  );

  async function deleteRecipient() {
    return recipientService
      .changeDefaultRecipient(myRecipientsData.recipients[0].recipientId)
      .then((response) => {
        if (response.acknowledged) {
          return recipientService
            .deleteRecipientById(recipient.recipientId)
            .then(() => {
              dispatch(
                changePagination({
                  pageSize: myRecipientsData.pageSize,
                  currentPage: 0,
                }),
              );
              dispatch(getMyRecipients({ forceLoad: true }));
            });
        }
      });
  }

  return (
    <>
      <BarekamCard
        headerLabel={
          recipient.isMain ? (
            <MainRecipientHeaderLabel />
          ) : (
            <RecipientHeaderLabel />
          )
        }
        headerAction={
          <GapContainer>
            {recipient.isDefault ? (
              <BarekamChip
                messageKey={"default_recipient"}
                messageSection={MessageSections.USER}
                statusId={3}
                chipSize={"s"}
              />
            ) : (
              ""
            )}

            {recipient.isMain ? (
              <Tooltip
                title={getMessage(
                  "main_recipient_info",
                  MessageSections.ORDERS,
                  messageCache,
                  languageId,
                )}
              >
                <InfoOutlined />
              </Tooltip>
            ) : isReadonly ? (
              ""
            ) : (
              <ThreeDot>
                <MenuItem onClick={() => setDialogOpen(true)}>
                  <Message
                    messageKey={"delete"}
                    sectionId={MessageSections.ORDERS}
                  />
                </MenuItem>
                <MenuItem onClick={() => setRecipientToEdit?.(recipient)}>
                  <Message
                    messageKey={"edit"}
                    sectionId={MessageSections.ORDERS}
                  />
                </MenuItem>
              </ThreeDot>
            )}
          </GapContainer>
        }
        content={<CardContent />}
        onClick={onClick}
        withDarkHeader={recipient.isMain}
      />

      <ConfirmationDialog
        open={dialogOpen}
        handleClose={(confirmed) => {
          confirmed && deleteRecipient();
          setDialogOpen(false);
        }}
        titleKey={"recipient_delete_title"}
        descriptionKey={"recipient_delete_description"}
        isRemove
      />
    </>
  );
};

export const RecipientSkeletonCard = () => {
  return (
    <BarekamCard
      headerLabel={<></>}
      headerAction={<></>}
      content={
        <>
          <Skeleton width={"100%"} />
          <Skeleton width={"100%"} />
          <Skeleton width={"100%"} />
        </>
      }
      withDarkHeader={true}
    />
  );
};

interface RecipientCardProps {
  recipient: Recipient;
  isReadonly?: boolean;
  onClick: () => void;
  setRecipientToEdit?: Dispatch<SetStateAction<Recipient | undefined>>
}
